import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Game.css';
import './Profile.css';
import AuthLayeredCard from './AuthLayeredCard';
import { authButtonStyle, authButtonSmallStyle, authStatPanelStyle } from '../constants/authStyles';
import { useCharacter } from '../context/CharacterContext';
import { translateAbilityTypeName, translateCharacterName } from '../utils/translateGameData';
import { getOnboardingPath } from '../utils/introVideo';
import { fetchActiveArenaMatchId } from '../arena/activeMatch';

const Game = () => {
  const { t } = useTranslation('selection');
  const { t: tg } = useTranslation('gameData');
  const { t: tc } = useTranslation('common');
  const { character: userCharacter, loading, hasCharacter } = useCharacter();
  const [onlineCount, setOnlineCount] = useState(null);
  const [brokenMenuIcons, setBrokenMenuIcons] = useState({});
  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const menuItems = [
    { key: 'inventory', route: '/inventory', iconPath: '/image/menu/envanter.png', fallback: '🎒', labelKey: 'menuInventory' },
    { key: 'battle', route: '/battle', iconPath: '/image/menu/savas.png', fallback: '⚔️', labelKey: 'menuBattle' },
    { key: 'village', route: '/village', iconPath: '/image/menu/koy.png', fallback: '🏘️', labelKey: 'menuVillage' },
    { key: 'market', route: '/market', iconPath: '/image/menu/pazar.png', fallback: '🛒', labelKey: 'menuMarket' },
    { key: 'work', route: '/work', iconPath: '/image/menu/calisma.png', fallback: '🛠️', labelKey: 'menuWork' },
    { key: 'achievements', route: '/achievements', iconPath: '/image/menu/basarilar.png', fallback: '🏆', labelKey: 'menuAchievements' },
    { key: 'profile', route: '/profile', iconPath: '/image/menu/profil.png', fallback: '👤', labelKey: 'menuProfile' },
    { key: 'dungeon', route: '/dungeon', iconPath: '/image/menu/zindan.png', fallback: '🏰', labelKey: 'menuDungeon' },
    { key: 'arena', route: '/arena', iconPath: '/image/menu/arena.png', fallback: '🏟️', labelKey: 'menuArena' },
    { key: 'wiki', route: '/wiki', iconPath: '/image/menu/wiki.png', fallback: '📖', labelKey: 'menuWiki' },
  ];

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    if (!loading && !hasCharacter) {
      navigate(getOnboardingPath(username), { replace: true });
    }
  }, [username, navigate, loading, hasCharacter]);

  useEffect(() => {
    if (!username || loading || !hasCharacter) return undefined;
    let alive = true;
    (async () => {
      try {
        const matchId = await fetchActiveArenaMatchId(username);
        if (alive && matchId) {
          navigate('/battle', { replace: true, state: { arenaOnlineMatch: { matchId } } });
        }
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [username, loading, hasCharacter, navigate]);

  useEffect(() => {
    if (!username) return;

    let intervalId;

    const sendHeartbeatAndFetch = async () => {
      try {
        await axios.post('/api/online/heartbeat', { username });

        const res = await axios.get('/api/online/count');
        if (res.data && res.data.success) {
          setOnlineCount(res.data.count);
        }
      } catch (err) {
        console.error('Error updating online count:', err);
      }
    };

    sendHeartbeatAndFetch();
    intervalId = setInterval(sendHeartbeatAndFetch, 30000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [username]);

  const handleMenuClick = (route) => {
    navigate(route);
  };

  return (
    <div className="auth-container auth-container--selection">
      <AuthLayeredCard className="game-hub-card">
        <div className="game-hub-header">
          <h1 className="game-hub-title">{t('hubTitle')}</h1>
          {username && <PlayerMenu username={username} onGo={navigate} />}
          {onlineCount !== null ? (
            <span className="game-online-badge">{tc('online', { count: onlineCount })}</span>
          ) : (
            <span className="game-online-badge game-online-badge--placeholder" aria-hidden="true" />
          )}
        </div>

        {userCharacter && (
          <div className="game-hub-character">
            <div className="level-bar-section">
              <div className="level-bar-info">
                <span className="level-text">{tc('level')} {userCharacter.level}</span>
                <div className="game-level-bar-container">
                  <div
                    className="game-level-bar-fill"
                    style={{
                      width: `${userCharacter.expToNextLevel > 0
                        ? ((userCharacter.currentLevelExp || 0) / ((userCharacter.currentLevelExp || 0) + userCharacter.expToNextLevel)) * 100
                        : 100}%`,
                    }}
                  />
                  <div className="game-level-bar-text">
                    {userCharacter.currentLevelExp || 0} / {(userCharacter.currentLevelExp || 0) + userCharacter.expToNextLevel} {tc('exp')}
                  </div>
                </div>
              </div>
            </div>

            <div className="game-stats">
              <div className="game-stats-row">
                <div className="game-stat-panel" style={authStatPanelStyle}>
                  <span className="game-stat-label">{t('statClass')}</span>
                  <span className="game-stat-value">
                    {translateCharacterName(tg, userCharacter.characterId, userCharacter.characterName)}
                  </span>
                </div>
                <div className="game-stat-panel" style={authStatPanelStyle}>
                  <span className="game-stat-label">{t('statAbilityType')}</span>
                  <span className="game-stat-value">
                    {translateAbilityTypeName(tg, userCharacter.abilityTypeName)}
                  </span>
                </div>
              </div>
              <div className="game-stats-row game-stats-row--economy">
                <div className="game-stat-panel" style={authStatPanelStyle}>
                  <span className="game-stat-label">{t('statVillage')}</span>
                  <span className="game-stat-value">{userCharacter.villageName}</span>
                </div>
                <div className="game-stat-panel game-stat-panel--gold" style={authStatPanelStyle}>
                  <span className="game-stat-label">{tc('yang')}:</span>
                  <span className="game-stat-value">{userCharacter.gold || 0}</span>
                </div>
                <div className="game-stat-panel game-stat-panel--soul" style={authStatPanelStyle}>
                  <span className="game-stat-label">{tc('soulMoney')}:</span>
                  <span className="game-stat-value">{userCharacter.ruhParasi || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="game-menu">
          {menuItems.map((item) => (
            <button key={item.key} className="menu-button" onClick={() => handleMenuClick(item.route)}>
              {!brokenMenuIcons[item.key] ? (
                <img
                  className="menu-icon-image"
                  src={item.iconPath}
                  alt={t(item.labelKey)}
                  onError={() => setBrokenMenuIcons((prev) => ({ ...prev, [item.key]: true }))}
                />
              ) : (
                <span className="menu-icon">{item.fallback}</span>
              )}
              <span className="menu-text">{t(item.labelKey)}</span>
            </button>
          ))}
        </div>

        <div className="game-footer">
          <button
            type="button"
            className="auth-button game-logout-button"
            style={authButtonStyle}
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('username');
              navigate('/login');
            }}
          >
            {tc('logout')}
          </button>
        </div>
      </AuthLayeredCard>
    </div>
  );
};

/** Oyuncu adına tıklayınca açılan menü: Profil, Arkadaşlar, Arena, Savaş Geçmişi. */
function PlayerMenu({ username, onGo }) {
  const { t } = useTranslation('social');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const items = [
    ['menuProfile', '/profile'],
    ['menuFriends', '/profile?tab=friends'],
    ['menuHistory', '/profile?tab=history'],
  ];
  return (
    <div className="player-menu" ref={ref}>
      <button type="button" className="player-menu-trigger" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="game-hub-username">{username}</span>
        <span className="player-menu-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="player-menu-list" role="menu">
          {items.map(([key, route]) => (
            <button key={key} type="button" role="menuitem" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => { setOpen(false); onGo(route); }}>
              {t(key)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default Game;
